import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import tw from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { atom, useAtom, useAtomValue } from 'jotai';

import { isValidPhoneNumber, Select, TextArea, TextInput } from '@restworld/ui-ds';
import { useVariableOptions } from '@restworld/utils-common';
import { Picture, Variable } from '@restworld/utility-types';
import { PhoneInput } from '@restworld/ui-ds-base';

import RestaurantOnboardingOpeningHours from './restaurant-opening-hours';
import RestaurantOnboardingImageGallery from './restaurant-onboarding-image-gallery';
import { useRestaurantOnboardingData } from './use-restaurant-onboarding-data-query';

export type RestaurantOnboardingPayload = {
  restaurant_name: string;
  address: string;
  brief_description: string;
  covers_per_service: string;
  staff_size_floor: number;
  staff_size_kitchen: number;
  instagram_url: string;
  facebook_url: string;
  website_url: string;
  the_fork_url: string;
  opening_hours: string;
  pictures: Picture[];
  number_of_staffs: string;
  binary_pictures: File[];
  business_address: string;
  // user contact information
  name: string;
  surname: string;
  email: string;
  phone_number: string;
  privacy_policy: boolean;
  terms_and_conditions: boolean;
};
export const coverPerServiceOptionsAtom = atom<Variable[]>([]);
export const useCoverPerServiceOptions = () => {
  const { variableOptions, fetchVariable } = useVariableOptions();
  const [coverPerServiceOptions, setCoverPerService] = useAtom(coverPerServiceOptionsAtom);

  const coverPerServicesData = variableOptions.covers_per_service;

  React.useEffect(() => {
    if (!coverPerServiceOptions?.length) fetchVariable(['covers_per_service']);
  }, [fetchVariable, coverPerServiceOptions?.length]);

  React.useEffect(() => {
    if (coverPerServicesData) setCoverPerService(coverPerServicesData);
  }, [setCoverPerService, coverPerServicesData]);
};
export const RestaurantOnboardingFormSection: React.FunctionComponent<{
  label: string;
  placeholder: string;
  name: keyof RestaurantOnboardingPayload;
  options?: Variable[];
  type?: 'text' | 'number' | 'url' | 'email' | 'textarea';
  required?: boolean | string;
}> = ({ label, placeholder, name, options, type = 'text', required }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<RestaurantOnboardingPayload>();
  const { t } = useTranslation('restaurant-onboarding');
  const formElement = React.useMemo(() => {
    if (name === 'phone_number')
      return (
        <Controller
          name="phone_number"
          control={control}
          // rules={{ required }}
          rules={{
            required,
            validate: (phoneNumber) => {
              if (!!phoneNumber && phoneNumber?.length >= 4)
                return isValidPhoneNumber(phoneNumber) || t('enter_valid_phone_number');
              // Checking the string length to know if the user has started enterting any number or not (by default, the country code is added to the value)
              else if (!!phoneNumber && phoneNumber?.length < 4) return t('enter_valid_phone_number');
            },
          }}
          render={({ field }) => (
            <>
              <PhoneInput
                {...field}
                id="phone_number"
                textInput={{
                  placeholder,
                  supportText: errors['phone_number']?.message,
                  supportTextVariant:
                    errors['phone_number']?.message && field?.value?.length >= 4 ? 'error' : 'default',
                  wrapperStyle: tw`border-neutral-100 border w-full`,
                  isFullwidth: true,
                }}
              />
              <p tw="text-neutral-300">*Ci serve per verificare che sia tu il vero gestore del locale</p>
            </>
          )}
        />
      );
    if (name === 'covers_per_service')
      return (
        <Controller
          name="covers_per_service"
          control={control}
          rules={{ required }}
          render={({ field }) => (
            <Select
              fullwidth
              options={
                options
                  ? options.map((v) => ({
                      value: v.id,
                      label: v.label,
                    }))
                  : []
              }
              placeholder="100"
              {...field}
              onChange={({ value }) => field.onChange(value)}
              value={{
                value: field.value,
                label: options ? options?.find((v) => v.id === field.value)?.label : '',
              }}
            />
          )}
        />
      );
    return (
      <Controller
        control={control}
        name={name}
        rules={{
          required,
          ...(type === 'url'
            ? {
                pattern: {
                  value: /^(https?:\/\/)([\w-]+\.)+[\w-]+(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/,
                  message: t('url_not_valid'),
                },
              }
            : type === 'email'
            ? {
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: t('enter_valid_email'),
                },
              }
            : {}),
        }}
        render={({ field }) =>
          name === 'brief_description' ? (
            <div tw="w-full">
              <TextArea
                id=""
                type="textarea"
                placeholder={placeholder}
                helperText={errors[name]?.message}
                color={Object.keys(errors).includes(name) ? 'failure' : undefined}
                twStyle={tw`min-w-full w-full`}
                rows={8}
                {...field}
                value={field.value?.toString() || ''} // Ensure value is always a string
              />
            </div>
          ) : (
            <TextInput
              {...field}
              value={typeof field.value === 'string' || typeof field.value === 'number' ? field.value : ''} // Ensure value is string or number
              placeholder={placeholder}
              id=""
              {...{ bordercolor: 'neutral-90', twStyle: tw`w-full` }}
              type={type}
              supportText={errors[name]?.message}
              isError={!!errors[name]}
            />
          )
        }
      />
    );
  }, [control, errors, name, options, placeholder, required, t, type]);
  return (
    <div tw="self-stretch inline-flex flex-col justify-start items-start gap-2">
      <h5 tw="justify-center text-neutral-700 text-base font-medium">{label}</h5>
      {formElement}
      {name === 'email' && <p tw="text-neutral-300">*Ti avviseremo qui quando ricevi candidature</p>}
    </div>
  );
};

const FormHeader: React.FunctionComponent = () => {
  const { t } = useTranslation('restaurant-onboarding');
  return (
    <div tw="self-stretch inline-flex flex-col justify-start items-start gap-4">
      <h5 tw="justify-start text-neutral-700 text-xl font-semibold">{t('heres_what_we_found')}</h5>
      <h5 tw="self-stretch justify-start text-neutral-200 text-sm font-medium">
        {t('you_can_edit_this_data_later_from_the_venue_detail_page')}
      </h5>
    </div>
  );
};

const RestaurantDescriptionForms = () => {
  const { t } = useTranslation('restaurant-onboarding');
  return (
    <div tw="self-stretch inline-flex flex-col justify-start items-start gap-6 w-full">
      <RestaurantOnboardingFormSection
        label={t('name_of_the_venue')}
        placeholder="Lo Scoglio"
        name="restaurant_name"
        required={t('restaurant_name_is_required')}
      />
      <RestaurantOnboardingFormSection
        label={t('venue_address')}
        placeholder="Via Po 9, 10001, Torino"
        name="address"
      />
      <RestaurantOnboardingFormSection
        label={t('description')}
        placeholder="Lorem ipsum, nata a Torino nel 1997, la Gelateria Artigianale Baires produce ogni giorno da più di 23 anni il suo gelato."
        name="brief_description"
        type="textarea"
      />
    </div>
  );
};

const RestaurantStaffForms = () => {
  useCoverPerServiceOptions();
  const coverServices = useAtomValue(coverPerServiceOptionsAtom);
  return (
    <div tw="self-stretch inline-flex flex-col justify-start items-start gap-6 w-full">
      <RestaurantOnboardingFormSection
        label={useTranslation('restaurant-onboarding').t('staff_members')}
        placeholder="17"
        name="number_of_staffs"
        type="number"
      />
      <RestaurantOnboardingFormSection
        label={useTranslation('restaurant-onboarding').t('covers')}
        placeholder="Via Po 9, 10001, Torino"
        name="covers_per_service"
        options={coverServices}
      />
    </div>
  );
};

const RestaurantLinksForm = () => {
  const restaurantData = useRestaurantOnboardingData();
  return (
    <div tw="self-stretch inline-flex flex-col justify-start items-start gap-6 w-full">
      {[
        ...(restaurantData?.website_url
          ? [{ label: 'Website', placeholder: 'https://www.loscoglio.com', name: 'website_url' }]
          : []),
        ...(restaurantData?.instagram_url
          ? [{ label: 'Instagram', placeholder: 'https://www.instagram.com/loscoglio', name: 'instagram_url' }]
          : []),
        ...(restaurantData?.facebook_url
          ? [{ label: 'Facebook', placeholder: 'https://www.facebook.com/loscoglio', name: 'facebook_url' }]
          : []),
        ...(restaurantData?.the_fork_url
          ? [{ label: 'The Fork', placeholder: 'https://www.thefork.com/loscoglio', name: 'the_fork_url' }]
          : []),
      ].map(({ label, placeholder, name }) => (
        <RestaurantOnboardingFormSection
          key={name}
          label={label}
          placeholder={placeholder}
          name={name as keyof RestaurantOnboardingPayload}
          type="url"
        />
      ))}
    </div>
  );
};
const RestaurantOnboardingForm: React.FunctionComponent = () => {
  return (
    <div tw="flex flex-col gap-12 h-full">
      <FormHeader />
      <RestaurantDescriptionForms />
      <hr />
      <RestaurantOnboardingOpeningHours />
      <hr />
      <RestaurantOnboardingImageGallery />
      <hr />
      <RestaurantStaffForms />
      <hr />
      <RestaurantLinksForm />
    </div>
  );
};

export default RestaurantOnboardingForm;
